import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { getFormattedUTCDate } from "helpers";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ItemsTable, TableItemActionsControl } from "ui";

import {
  SharingScheduleRuleTimeframeLabelMap,
  SharingScheduleRulesLabelsMap
} from "../../constants";
import { SharingSchedulesListItem } from "../../sharing.types";
import { SharingSchedulesTableProps } from "./SharingSchedulesTable.types";

import styles from "./SharingSchedulesTable.module.scss";

const SharingSchedulesTable = ({
  schedules,
  sorting,
  setSorting,
  onDeleteSchedule,
  onEditSchedule,
  isLoading
}: SharingSchedulesTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const columns: Array<
    ColumnDef<SharingSchedulesListItem, SharingSchedulesListItem[]>
  > = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => schedules[row.index].name
      },
      {
        id: "FromDate",
        accessorKey: "FromDate",
        header: t("First Day"),
        size: 6,
        minSize: 6,
        cell: ({ row }) =>
          getFormattedUTCDate(schedules[row.index].from, "DD/MM/YYYY")
      },
      {
        id: "ToDate",
        accessorKey: "ToDate",
        header: t("Last Day"),
        size: 6,
        minSize: 6,
        cell: ({ row }) =>
          getFormattedUTCDate(schedules[row.index].to, "DD/MM/YYYY")
      },
      {
        header: t("Products Qty"),
        size: 3,
        minSize: 3,
        cell: ({ row }) => schedules[row.index].numberOfProducts
      },
      {
        header: t("Rule"),
        size: 16,
        minSize: 16,
        cell: ({ row }) =>
          t(SharingScheduleRulesLabelsMap[schedules[row.index].rule])
      },
      {
        header: t("Timeframes Qty"),
        size: 3,
        minSize: 3,
        cell: ({ row }) => schedules[row.index].ruleTimeNumber ?? "-"
      },
      {
        header: t("Rule's timeframe"),
        size: 12,
        minSize: 12,
        cell: ({ row }) =>
          SharingScheduleRuleTimeframeLabelMap[
            schedules[row.index].ruleTimeframe
          ]
            ? t(
                SharingScheduleRuleTimeframeLabelMap[
                  schedules[row.index].ruleTimeframe
                ]
              )
            : "-"
      },
      {
        header: t("Products selected"),
        size: 20,
        minSize: 20,
        cell: ({ row }) => (
          <div className={styles.table_productsCell}>
            {schedules[row.index].productVariants.map((product) => (
              <div key={product.id}>{`${product.name}\n`}</div>
            ))}
          </div>
        )
      },

      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 2,
        minSize: 2,
        cell: ({ row }) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: t("Edit"),
                Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                onClick: () => onEditSchedule(schedules[row.index])
              },
              {
                label: t("Delete"),
                Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: () => onDeleteSchedule(schedules[row.index].id)
              }
            ]}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ),
        enableSorting: false
      }
    ],
    [schedules, openedItemOptionsIndex, currentLanguage]
  );

  return (
    <ItemsTable
      columns={columns}
      data={schedules}
      sorting={sorting}
      skeletonHeight={65}
      setSorting={setSorting}
      isLoading={isLoading}
    />
  );
};

export default SharingSchedulesTable;
